import { dispatch, updateBalance } from "@/store";
import type { UserBalance } from "@/types";

/**
 * Why the server says a wallet moved. Mirrors the backend's
 * `WalletUpdateReason`.
 */
export type WalletUpdateReason = "bet" | "win" | "refund" | "payment";

/**
 * The gate between the server's wallet pushes and the figure on screen.
 *
 * The backend settles a house game inside the request that places the bet - it
 * has to, because the outcome comes from the seed position the bet claims - and
 * it pushes the authoritative wallet on both the debit and the credit. Those
 * two pushes are milliseconds apart. The reveal they belong to is seconds long:
 * the ball is still falling, the wheel is still turning, the dealer has not
 * drawn out yet.
 *
 * So a page that paces its own history and its own wallet still loses, because
 * the push arrives behind its back and prints the answer. This is the one place
 * that can stop it: while a reveal is running, a `win` push is held, and it
 * lands the moment the board has finished showing what earned it.
 *
 * Only `win` is held. A stake leaving is the player's own action and confirms
 * their bet went in; a refund and a deposit are not answers to anything. And
 * nothing here touches the ledger - the money is already theirs the moment the
 * server books it. This is about when they are *shown* it.
 *
 * One known seam, and it is plinko's alone: a `bet` push carries the whole
 * wallet, so accepting the stake for a second ball while the first is still
 * falling shows the first one's payout early. Holding `bet` as well would fix
 * it and cost more than it is worth - the push is also what corrects a stake
 * part-funded from bonus, which the page cannot work out for itself - and
 * plinko is the one game built to have a dozen rounds in the air at once, where
 * a player cannot attribute a change to any single ball anyway.
 */

/** Reveals currently running. Non-zero means a `win` push has to wait. */
let revealing = 0;

/** The newest held figure, or null when there is nothing waiting. */
let queued: Partial<UserBalance> | null = null;

/** Safety net for a reveal that never reports back; see `hold`. */
let fallback: ReturnType<typeof setTimeout> | null = null;

/**
 * How long a held figure may wait before it is shown anyway.
 *
 * Every reveal in the casino is comfortably under this. It exists so that a bug
 * in one game - a reveal that throws before it releases, a board unmounted
 * mid-animation - costs the player a few seconds of a stale balance rather than
 * the rest of their session.
 */
const MAX_HOLD_MS = 15000;

const flush = () => {
  if (fallback) {
    clearTimeout(fallback);
    fallback = null;
  }

  if (!queued) return;

  const figure = queued;
  queued = null;

  dispatch(updateBalance(figure));
};

/** Starts holding `win` pushes. Pair every call with a `release`. */
export const holdWinnings = (): void => {
  revealing += 1;

  if (!fallback) fallback = setTimeout(flush, MAX_HOLD_MS);
};

/** Stops holding, showing whatever came in while the reveal was running. */
export const releaseWinnings = (): void => {
  revealing = Math.max(0, revealing - 1);

  if (revealing === 0) flush();
};

/**
 * Applies one `wallet:update` push, holding it back if it is an answer the
 * player has not been shown the reason for yet.
 */
export const applyWalletPush = (
  figure: Partial<UserBalance>,
  reason: WalletUpdateReason = "payment",
): void => {
  if (reason === "win" && revealing > 0) {
    // Only the newest is worth keeping: each push is the whole wallet as it
    // stood, so a later one already contains everything an earlier one said.
    queued = figure;

    if (!fallback) fallback = setTimeout(flush, MAX_HOLD_MS);

    return;
  }

  dispatch(updateBalance(figure));
};
